/**
 * MODULE: _shared/frontend-shell/src/design-system/components/AiAssistant/assistantContentPartition.ts
 * PURPOSE: Strip optional Langflow `<!-- brazn-sources ... -->` machine block and parse structured citations (Perplexity-style).
 *
 * Langflow contract: append after the answer (plain text / markdown), e.g.
 * `<!-- brazn-sources [{"url":"https://…","title":"…","snippet":"…"}] -->`
 * or `<!-- brazn-sources { "items": [ … ] } -->`
 */

import { extractSourceUrls } from './extractSourceUrls';

export interface AssistantStructuredSource {
  url: string;
  title?: string;
  snippet?: string;
}

const OPEN = /<!--\s*brazn-sources\s*/i;

function normalizeUrlKey(href: string): string {
  try {
    const u = new URL(href.trim());
    u.hash = '';
    return u.href.toLowerCase();
  } catch {
    return href.trim().toLowerCase();
  }
}

function coerceSource(row: unknown): AssistantStructuredSource | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const url = typeof o.url === 'string' ? o.url.trim() : '';
  if (!/^https?:\/\//i.test(url)) return null;
  const title = typeof o.title === 'string' ? o.title.trim() : undefined;
  const snippet = typeof o.snippet === 'string' ? o.snippet.trim() : undefined;
  return { url, title: title || undefined, snippet: snippet || undefined };
}

function parseInner(jsonStr: string): AssistantStructuredSource[] {
  const trimmed = jsonStr.trim();
  if (!trimmed) return [];
  let data: unknown;
  try {
    data = JSON.parse(trimmed) as unknown;
  } catch {
    return [];
  }
  const rows: unknown[] = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).items)
      ? ((data as Record<string, unknown>).items as unknown[])
      : [];
  const out: AssistantStructuredSource[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const s = coerceSource(row);
    if (!s) continue;
    const k = normalizeUrlKey(s.url);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
    if (out.length >= 12) break;
  }
  return out;
}

/**
 * Returns markdown-safe `displayBody` (machine comment removed or hidden while incomplete)
 * plus structured sources for the Sources UI.
 */
export function partitionAssistantContent(raw: string): {
  displayBody: string;
  structuredSources: AssistantStructuredSource[];
} {
  if (!raw || typeof raw !== 'string') {
    return { displayBody: '', structuredSources: [] };
  }

  const m = raw.match(OPEN);
  if (!m || m.index === undefined) {
    return { displayBody: raw, structuredSources: [] };
  }

  const start = m.index;
  const fromOpen = raw.slice(start);
  const closeRel = fromOpen.indexOf('-->');
  if (closeRel < 0) {
    /* Streaming: hide everything from the opening tag until the comment is closed. */
    return { displayBody: raw.slice(0, start).trimEnd(), structuredSources: [] };
  }

  const inner = fromOpen.slice(m[0].length, closeRel).trim();
  const structuredSources = parseInner(inner);
  const displayBody = raw.slice(0, start).trimEnd();
  return { displayBody, structuredSources };
}

/** URLs already covered by structured sources (for deduping markdown URL chips). */
export function structuredUrlKeys(sources: AssistantStructuredSource[]): Set<string> {
  return new Set(sources.map((s) => normalizeUrlKey(s.url)));
}

/** Markdown / bare URLs in the visible body, excluding URLs already listed as structured sources. */
export function extraMarkdownSourceUrls(
  displayBody: string,
  structured: AssistantStructuredSource[]
): string[] {
  const keys = structuredUrlKeys(structured);
  return extractSourceUrls(displayBody).filter((u) => !keys.has(normalizeUrlKey(u)));
}
